import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchSeason } from '../data/api'
import type { Season, SeasonHeat, ScoutMapAgg } from '../types'
import { SectionLabel } from './RoundTimeline'
import { transformFor } from '../data/radar'
import { UtilMap } from './UtilMap'
import { Economy } from './Economy'
import { Practice } from './Practice'
import { Lineups } from './Lineups'
import { SpacingCurve } from './SpacingCurve'
import { clutchTitle, fmtDate, fmtStamp, median, paceLabel } from '../data/fmt'
import { useCanCoach } from '../data/session'

const card = { borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' } as const

/** The names this account actually played under, minus the one already shown as the row's title. */
const inGameNames = (p: { name: string; nick?: string; aliases?: string[] }): string[] =>
  [...(p.nick ? [p.name] : []), ...(p.aliases ?? [])].filter((n, i, a) => n && a.indexOf(n) === i)

/** Our own form across matches — the question a match page can't answer. Same fold as scouting,
 *  pointed at us, plus the coach's actual question: do our timeouts do anything? */
export function SeasonView({ onSelect }: { onSelect: (id: string) => void }) {
  const [data, setData] = useState<Season | null>(null)
  const [league, setLeague] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const canCoach = useCanCoach()

  const load = useCallback(
    (lg: string | null, refresh = false) => {
      setBusy(true)
      setErr(null)
      fetchSeason(lg, refresh)
        .then(setData)
        .catch((e) => setErr((e as Error).message))
        .finally(() => setBusy(false))
    },
    [],
  )
  useEffect(() => load(league), [load, league])

  const agg = data?.aggregate
  // A coach who stood in for a match is in the fold and should be — those rounds happened. They
  // just don't belong in a table headed "players this season".
  const ourPlayers = (agg?.players ?? []).filter((p) => !(data?.staff ?? []).includes(p.steamid64))
  const t = data?.timeouts
  const w = data?.matches.filter((m) => m.us > m.them).length ?? 0
  const l = data?.matches.filter((m) => m.us < m.them).length ?? 0

  return (
    <div className="space-y-5">
      <section className="border border-border bg-surface p-5" style={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionLabel n="Y0" label="Season" />
          <div className="flex items-center gap-2">
            <select
              value={league ?? 'all'}
              onChange={(e) => setLeague(e.target.value === 'all' ? null : e.target.value)}
              className="border border-border bg-bg-2 px-2 py-1 text-xs text-text outline-none"
              style={{ borderRadius: 'var(--radius)' }}
            >
              <option value="all">All competitions</option>
              {(data?.leagues ?? []).map((lg) => (
                <option key={lg} value={lg}>
                  {lg}
                </option>
              ))}
            </select>
            {canCoach && (
              /* A rebuild rewrites the season's AI self-review — coaches only, API included. */
              <button onClick={() => load(league, true)} className="text-xs text-text-dim hover:text-accent">
                rebuild
              </button>
            )}
          </div>
        </div>

        {err ? (
          <p className="mt-3 text-sm text-loss">{err}</p>
        ) : !data ? (
          <p className="mt-3 text-sm text-text-dim">{busy ? 'Folding matches…' : 'Loading…'}</p>
        ) : !data.matches.length ? (
          <p className="mt-3 text-sm text-text-faint">No matches of ours yet in this scope.</p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat label="Record" value={`${w}–${l}`} sub={`${data.matches.length} matches`} />
            <Stat
              label="Rounds"
              value={`${pct(agg?.maps.reduce((s, m) => s + m.roundsWon, 0) ?? 0, agg?.maps.reduce((s, m) => s + m.roundsPlayed, 0) ?? 0)}%`}
              sub={`${agg?.maps.reduce((s, m) => s + m.roundsWon, 0) ?? 0}/${agg?.maps.reduce((s, m) => s + m.roundsPlayed, 0) ?? 0} won`}
            />
            <Stat label="Maps played" value={String(agg?.maps.length ?? 0)} sub={agg?.maps.map((m) => m.map.replace('de_', '')).join(', ')} />
            <Stat label="Span" value={span(data)} sub={data?.league ? data.league.split('·').pop()?.trim() : 'all competitions'} />
          </div>
        )}
      </section>

      {/* What changed. A season page shows totals and a match page shows one game; "what's
          different since Tuesday" is the question that gets asked first and was answered nowhere. */}
      {!!data?.changes?.length && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y0b" label="Since the last match" />
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {data.changes.map((c) => {
              const good = c.delta > 0 === c.higherIsBetter
              return (
                <div key={c.label} className="flex items-baseline gap-2 text-sm">
                  <span
                    className="w-16 shrink-0 text-right font-display tabular-nums"
                    style={{ color: good ? 'var(--win)' : 'var(--loss)' }}
                  >
                    {c.delta > 0 ? '+' : ''}
                    {c.delta}
                    {c.unit === '%' ? '%' : ''}
                  </span>
                  <span className="text-text">{c.label}</span>
                  <span className="ml-auto text-[11px] tabular-nums text-text-faint">
                    {c.now}
                    {c.unit === '%' ? '%' : ''} vs {c.before}
                    {c.unit === '%' ? '%' : ''} before
                  </span>
                </div>
              )
            })}
          </div>
          <p className="mt-3 text-[11px] text-text-faint">
            Last match against the average of everything before it — not against the match before,
            which would make every result look like a swing. Changes too small to act on are left out.
          </p>
        </section>
      )}

      {/* Self-review across the period. The per-match AI review answers "how did that game go";
          this answers "what keeps happening", which is the one a coach plans practice from. */}
      {data?.report && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="YA" label="What the season says" />
          <p className="mt-3 text-sm leading-relaxed text-text">{data.report!.overview}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="font-display text-xs uppercase tracking-wider text-win">Working</div>
              <ul className="mt-2 space-y-1.5">
                {data.report!.strengths.map((x, i) => (
                  <li key={i} className="text-xs leading-relaxed text-text-dim">• {x}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-display text-xs uppercase tracking-wider text-loss">Work on</div>
              <ul className="mt-2 space-y-1.5">
                {data.report!.improve.map((x, i) => (
                  <li key={i} className="text-xs leading-relaxed text-text-dim">• {x}</li>
                ))}
              </ul>
            </div>
          </div>
          {data.report!.maps.length > 0 && (
            <div className="mt-4 space-y-2">
              {data.report!.maps.map((m) => (
                <div key={m.map} className="border-l-2 border-border pl-3">
                  <span className="font-display text-xs uppercase tracking-wider text-accent">
                    {m.map.replace('de_', '')}
                  </span>
                  <p className="mt-1 text-xs leading-relaxed text-text-dim">{m.read}</p>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[11px] text-text-faint">
            Generated by {data.report!.model ?? 'AI'}
            {data.report!.generatedAt ? ` · ${fmtStamp(data.report!.generatedAt)}` : ''}. Written from
            the same numbers on this page — if something here surprises you, check the table it came
            from.
          </p>
        </section>
      )}

      {/* Timeouts — the coach's question, which only makes sense across matches. */}
      {t && t.matches > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y1" label="Do our timeouts work?" />
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat label="We called" value={String(t.called)} sub={`over ${t.matches} match${t.matches === 1 ? '' : 'es'}`} />
            <Stat
              label="Broke their run"
              value={t.called ? `${t.brokeStreak}/${t.called}` : '—'}
              sub={t.called ? `${pct(t.brokeStreak, t.called)}% of ours` : 'none called'}
            />
            <Stat
              label="Next round won"
              value={t.afterTimeoutPlayed ? `${t.afterTimeoutWon}/${t.afterTimeoutPlayed}` : '—'}
              sub="after any timeout"
            />
            <Stat
              label="Next 3 rounds"
              value={t.next3Played ? `${pct(t.next3Won, t.next3Played)}%` : '—'}
              sub={`${t.next3Won}/${t.next3Played} won`}
            />
          </div>
          <p className="mt-3 text-xs text-text-faint">
            Opponents called {t.againstCalled} and broke our run {t.againstBrokeStreak} time
            {t.againstBrokeStreak === 1 ? '' : 's'}.{' '}
            {t.called < 5 && (
              <span className="text-loss">
                {t.called} timeout{t.called === 1 ? '' : 's'} is far too small a sample to conclude anything — this
                gets meaningful around 15–20.
              </span>
            )}
          </p>

          {/* The numbers above can only see the timeouts that HAPPENED. This is the other half:
              where we were three rounds into a slide with the pause still in our pocket. It's the
              part that changes what you do next week, rather than describing what you already did. */}
          {(t.missed?.length ?? 0) > 0 && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="text-[11px] uppercase tracking-wider text-text-faint">
                Slides nobody stopped
              </p>
              <ul className="mt-2 space-y-1 text-xs text-text-dim">
                {t.missed!.slice(0, 8).map((m) => (
                  <li key={`${m.demoId}-${m.from}`}>
                    <a href={`#/match/${m.demoId}`} className="text-accent hover:underline">
                      {m.map.replace('de_', '')}
                      {m.opponent ? ` vs ${m.opponent}` : ''}
                    </a>{' '}
                    — lost {m.length} straight from round {m.from}
                    {m.toEndOfMatch ? ', to the end of the match' : ', no timeout called'}
                  </li>
                ))}
              </ul>
              {t.missed!.length > 8 && (
                <p className="mt-1 text-[11px] text-text-faint">
                  …and {t.missed!.length - 8} more.
                </p>
              )}
              <p className="mt-2 text-[11px] leading-relaxed text-text-faint">
                Three or more rounds lost in a row with no tactical pause. A slide that ran to the
                final round is listed too, marked as such — there was nothing left to spend it on,
                so it isn't the same mistake.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Whose season is this? A roster change mid-season turns every per-map number below into
          two teams' numbers, and nothing else on the page would say so. */}
      {agg?.lineups && agg.lineups.length > 1 && <Lineups rows={agg.lineups} who="us" />}

      {/* Players — the fold has rating/ADR/openings/AWP/clutch/util per player; show it. */}
      {agg && ourPlayers.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y2" label="Players this season" />
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-160 border-collapse text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-text-faint">
                  <th className="px-2 py-1.5 text-left font-medium">Player</th>
                  <th className="px-2 py-1.5 text-left font-medium" title="Rating per match, oldest → newest">Form</th>
                  <th className="px-2 py-1.5 text-right font-medium">Rating</th>
                  <th className="px-2 py-1.5 text-right font-medium">ADR</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Rounds with a kill, assist, survival or traded death">KAST</th>
                  <th className="px-2 py-1.5 text-right font-medium">K–D</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Opening duels won-lost on CT">Open CT</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Opening duels won-lost on T">Open T</th>
                  <th className="px-2 py-1.5 text-right font-medium">AWP</th>
                  <th
                    className="px-2 py-1.5 text-right font-medium"
                    title="Trade kills / deaths of ours that got traded / deaths nobody traded"
                  >
                    Trades
                  </th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Shots→hits accuracy · headshot %">Acc·HS</th>
                  <th className="px-2 py-1.5 text-right font-medium">Clutch</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="flash / smoke / HE / molotov">Util</th>
                  <th className="px-2 py-1.5 text-right font-medium">Top</th>
                </tr>
              </thead>
              <tbody>
                {[...ourPlayers]
                  .sort((a, b) => b.rating - a.rating)
                  .map((p) => (
                    <tr key={p.steamid64 || p.name} className="border-t border-border/60 hover:bg-surface-2">
                      <td className="px-2 py-2 font-medium text-text">
                        {p.nick ?? p.name}
                        <span className="ml-1.5 text-[10px] text-text-faint">{p.demos}d</span>
                        {/* Every name this steamid has used in a demo. With a roster nick set, the
                            in-game name is one of them — the row is the account, not the label. */}
                        {inGameNames(p).length > 0 && (
                          <div
                            className="text-[10px] font-normal text-text-faint"
                            title={`Same account, in-game names: ${inGameNames(p).join(', ')}`}
                          >
                            aka {inGameNames(p).slice(0, 2).join(', ')}
                            {inGameNames(p).length > 2 ? ` +${inGameNames(p).length - 2}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <Spark points={(data?.trends?.[p.steamid64] ?? []).map((t) => t.rating)} />
                      </td>
                      <td
                        className="px-2 py-2 text-right font-display tabular-nums"
                        style={{ color: p.rating >= 1 ? 'var(--accent)' : 'var(--loss)' }}
                      >
                        {p.rating.toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{p.adr}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim">{p.kast}%</td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim">
                        {p.kills}–{p.deaths}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim">
                        {p.openingCT.k}-{p.openingCT.d}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim">
                        {p.openingT.k}-{p.openingT.d}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim">{p.awpKills || '–'}</td>
                      <td
                        className="px-2 py-2 text-right text-[11px] tabular-nums"
                        title={`${p.tradeKills} trade kills · ${p.tradedDeaths} of your deaths were traded · ${p.untradedDeaths} weren't`}
                      >
                        <span className="text-accent">{p.tradeKills}</span>
                        <span className="text-text-faint">/</span>
                        <span className="text-text-dim">{p.tradedDeaths}</span>
                        <span className="text-text-faint">/</span>
                        <span style={{ color: 'var(--loss)' }}>{p.untradedDeaths}</span>
                      </td>
                      <td className="px-2 py-2 text-right text-[11px] tabular-nums text-text-dim">
                        {p.acc}% · {p.hsPct}%
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-text-dim" title={clutchTitle(p.clutchBy)}>
                        {p.clutchP ? `${p.clutchW}/${p.clutchP}` : '–'}
                      </td>
                      <td
                        className="px-2 py-2 text-right text-[11px] tabular-nums text-text-faint"
                        title={`${p.util.flashes}F ${p.util.smokes}S ${p.util.he}HE ${p.util.molly}M · ${p.util.enemiesFlashed} enemies flashed · ${p.util.teamFlashed} TEAMMATES flashed · ${p.util.utilDmg} util damage`}
                      >
                        {p.util.flashes}/{p.util.smokes}/{p.util.he}/{p.util.molly}
                        {p.util.teamFlashed > 0 && (
                          <span style={{ color: 'var(--loss)' }} title="teammates blinded"> ·{p.util.teamFlashed}⚠</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-[11px] text-text-dim">{wep(p.topWeapon)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Per-map — economy, util and our OWN site split: the same read we run on opponents,
          turned on ourselves. "Are we predictable?" is a question only this can answer. */}
      {agg && agg.maps.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y3" label="Maps" />
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {[...agg.maps]
              .sort((a, b) => b.demos - a.demos)
              .map((m: ScoutMapAgg) => {
                const site = Object.entries(m.sites.bySite).sort((a, b) => b[1] - a[1])
                const plants = m.sites.plants
                const fav = site[0]
                const favPct = plants && fav ? Math.round((100 * fav[1]) / plants) : 0
                return (
                  <div key={m.map} className="border border-border bg-bg-2 p-4" style={{ borderRadius: 'var(--radius)' }}>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm uppercase tracking-wider text-accent">{m.map}</span>
                      <span className="font-display tabular-nums text-sm">
                        {m.wins}–{m.losses}
                      </span>
                      <span
                        className="text-xs tabular-nums"
                        style={{ color: m.roundWinPct >= 50 ? 'var(--accent)' : 'var(--loss)' }}
                      >
                        {m.roundWinPct}% rounds
                      </span>
                      <span className="ml-auto text-[10px] text-text-faint">
                        {m.demos} demo{m.demos === 1 ? '' : 's'}
                        {m.demos < 3 ? <span className="ml-1 text-loss">thin</span> : null}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                      <Row k="Opening CT" v={`${m.opening.CT.k}-${m.opening.CT.d}`} good={m.opening.CT.k >= m.opening.CT.d} />
                      <Row k="Opening T" v={`${m.opening.T.k}-${m.opening.T.d}`} good={m.opening.T.k >= m.opening.T.d} />
                      <Row
                        k="Plant rate"
                        v={m.sites.tRounds ? `${Math.round((100 * plants) / m.sites.tRounds)}% (${plants}/${m.sites.tRounds} T)` : '—'}
                      />
                      <Row
                        k="Site split"
                        v={site.length ? site.map(([x, n]) => `${x} ${n}`).join(' · ') : '—'}
                        warn={favPct >= 70 && plants >= 5}
                      />
                    </div>

                    {/* The joins: first blood → round, and bomb down → closed. We had both halves
                        of each all along and never put them together. */}
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-border/60 pt-2 text-[11px]">
                      <Row
                        k="Win opening → round"
                        v={cv(m.conv.openWon)}
                        good={m.conv.openWon.n > 0 && m.conv.openWon.won * 2 >= m.conv.openWon.n}
                      />
                      <Row
                        k="Lose opening → round"
                        v={cv(m.conv.openLost)}
                        good={m.conv.openLost.n > 0 && m.conv.openLost.won * 2 >= m.conv.openLost.n}
                      />
                      <Row k="Post-plant closed" v={cv(m.conv.postPlant)} good={m.conv.postPlant.won * 2 >= m.conv.postPlant.n} />
                      <Row k="Retakes won" v={cv(m.conv.retake)} good={m.conv.retake.won * 2 >= m.conv.retake.n} />
                      {(m.toSite ?? []).map((ts) => {
                        const md = median(ts.secs)
                        return md === null ? null : (
                          <Row key={ts.site} k={`Pace to ${ts.site}`} v={`${md.toFixed(0)}s · ${paceLabel(md)} (${ts.secs.length})`} />
                        )
                      })}
                      {m.spacing?.T?.n > 0 && (
                        <Row k="Spacing T · CT" v={`${Math.round(m.spacing.T.sum / m.spacing.T.n)} · ${m.spacing.CT.n ? Math.round(m.spacing.CT.sum / m.spacing.CT.n) : '—'}`} />
                      )}
                    </div>

                    {Object.keys(m.reasons).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(m.reasons)
                          .sort((a, b) => b[1].w + b[1].l - (a[1].w + a[1].l))
                          .map(([reason, v]) => (
                            <span
                              key={reason}
                              className="border border-border px-1.5 py-0.5 text-[10px] tabular-nums text-text-faint"
                              style={{ borderRadius: 'var(--radius)' }}
                              title={`${REASON[reason] ?? reason}: won ${v.w}, lost ${v.l}`}
                            >
                              {REASON[reason] ?? reason} <span className="text-accent">{v.w}</span>/
                              <span style={{ color: 'var(--loss)' }}>{v.l}</span>
                            </span>
                          ))}
                      </div>
                    )}

                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-text-faint">Buys (won/played)</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {Object.entries(m.economy).map(([type, v]) => (
                          <span
                            key={type}
                            className="border border-border px-1.5 py-0.5 text-[11px] tabular-nums"
                            style={{ borderRadius: 'var(--radius)', color: v.w * 2 >= v.p ? 'var(--accent)' : 'var(--text-dim)' }}
                            title={`${type}: ${v.w} won of ${v.p}`}
                          >
                            {type.toLowerCase()} {v.w}/{v.p}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-2 text-[11px] text-text-faint">
                      Util: {m.util.flashes}F · {m.util.smokes}S · {m.util.he}HE · {m.util.molly}M ·{' '}
                      {m.util.flashAssists} flash assists
                    </div>

                    {m.players.length > 0 && (
                      <div className="mt-3 border-t border-border/60 pt-2">
                        <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint">
                          On {m.map.replace('de_', '')}
                        </div>
                        <div className="space-y-1">
                          {[...m.players]
                            .sort((a, b) => b.rating - a.rating)
                            .map((p) => (
                              <div key={p.name} className="flex gap-2 text-[11px] tabular-nums">
                                <span className="w-24 truncate text-text">{p.name}</span>
                                <span style={{ color: p.rating >= 1 ? 'var(--accent)' : 'var(--loss)' }}>
                                  {p.rating.toFixed(2)}
                                </span>
                                <span className="text-text-dim">{p.adr} adr</span>
                                <span className="text-text-faint">
                                  open {p.openingCT.k + p.openingT.k}-{p.openingCT.d + p.openingT.d}
                                </span>
                                {p.awpKills > 0 && <span className="text-text-faint">{p.awpKills} awp</span>}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
          <p className="mt-3 text-[11px] text-text-faint">
            This is the same read we run on opponents, pointed at us — a site split above 70% is how they'll be
            setting up against you.
          </p>
        </section>
      )}

      {/* Our own utility, same read. "Are we throwing the same smoke every round?" */}
      <Practice />

      {agg && agg.maps.some((m) => m.econDepth?.rounds) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y1b" label="Our economy, past the headline" />
          <Economy maps={agg.maps} who="we" />
        </section>
      )}

      {agg && agg.maps.some((m) => m.nades?.length) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y4" label="Where our utility lands" />
          <UtilMap maps={agg.maps} who="our" />
        </section>
      )}

      {agg && agg.maps.some((m) => m.spacingCurve && Object.keys(m.spacingCurve.T).length > 1) && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y4b" label="How we spread over the round" />
          <SpacingCurve maps={agg.maps} who="our" />
        </section>
      )}

      {/* Kill positions across the season. Per match this is the heatmap tab; the same data folded
          across matches answers "where do we keep dying on this map", which one demo never can. */}
      {data && data.heat.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y5" label="Where we kill and die" />
          <SeasonHeatmaps heat={data.heat} />
        </section>
      )}

      {/* Matches in scope */}
      {data && data.matches.length > 0 && (
        <section className="border border-border bg-surface p-5" style={card}>
          <SectionLabel n="Y6" label="Matches in this scope" />
          <div className="mt-3 divide-y divide-border">
            {data.matches.map((m) => (
              <button
                key={m.demoId}
                onClick={() => onSelect(m.demoId)}
                className="flex w-full items-center gap-4 py-2.5 text-left text-sm hover:bg-surface-2"
              >
                <span className="font-display w-20 uppercase tracking-wider text-text-dim">
                  {m.map.replace('de_', '')}
                </span>
                <span className="font-display tabular-nums">
                  <span style={{ color: m.us > m.them ? 'var(--accent)' : 'var(--loss)' }}>{m.us}</span>–{m.them}
                </span>
                <span className="text-text-dim">{m.opponent}</span>
                <span className="ml-auto text-xs text-text-faint">
                  {m.finishedAt ? fmtDate(m.finishedAt) : ''}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function SeasonHeatmaps({ heat }: { heat: SeasonHeat[] }) {
  const [mapIdx, setMapIdx] = useState(0)
  const [side, setSide] = useState<'ours' | 'theirs'>('theirs')
  // Two-storey maps again: without this, nuke's lower-level deaths draw on the upper radar.
  const [layer, setLayer] = useState<'upper' | 'lower'>('upper')
  // "We die a lot on Anubis" and "our B-site CT setup gets run over" are different problems with
  // different fixes, and only one of them is actionable.
  const [ourSide, setOurSide] = useState<'all' | 'CT' | 'T'>('all')
  const h = heat[Math.min(mapIdx, heat.length - 1)]
  const layered = !!h && [...h.ours, ...h.theirs].some((p) => p[2])
  const onLayer = (l?: string | null) => !layered || (l ?? 'upper') === layer
  // Older artifacts carry no side; offer the filter only when the data can answer it.
  const hasSides = !!h && [...h.ours, ...h.theirs].some((p) => p[3])
  const onSide = (sd?: string | null) => ourSide === 'all' || sd === ourSide
  const shownPts = (pts: SeasonHeat['ours']) => pts.filter((p) => onLayer(p[2]) && onSide(p[3]))
  return (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {heat.map((x, i) => (
          <button
            key={x.map}
            onClick={() => setMapIdx(i)}
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: i === mapIdx ? 'var(--accent)' : 'transparent',
              color: i === mapIdx ? 'var(--accent-ink)' : 'var(--text-dim)',
            }}
          >
            {x.map.replace('de_', '')}
          </button>
        ))}
        <span className="mx-2 text-text-faint">|</span>
        {(['theirs', 'ours'] as const).map((sd) => (
          <button
            key={sd}
            onClick={() => setSide(sd)}
            className="px-2 py-1 text-xs"
            style={{
              borderRadius: 'var(--radius)',
              border: '1px solid var(--border)',
              background: side === sd ? (sd === 'ours' ? 'var(--accent)' : 'var(--loss)') : 'transparent',
              color: side === sd ? '#fff' : 'var(--text-dim)',
            }}
          >
            {sd === 'ours' ? 'our kills' : 'where we die'}
          </button>
        ))}
        {hasSides && (
          <>
            <span className="mx-1 text-text-faint">|</span>
            {(['all', 'CT', 'T'] as const).map((sd) => (
              <button
                key={sd}
                onClick={() => setOurSide(sd)}
                title={sd === 'all' ? 'Both sides' : `Rounds we played ${sd}`}
                className="px-2 py-1 text-xs"
                style={{
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  background: ourSide === sd ? 'var(--surface-2)' : 'transparent',
                  color: ourSide === sd ? 'var(--text)' : 'var(--text-dim)',
                }}
              >
                {sd === 'all' ? 'both sides' : `our ${sd}`}
              </button>
            ))}
          </>
        )}
        {layered && (
          <>
            <span className="mx-1 text-text-faint">|</span>
            {(['upper', 'lower'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLayer(l)}
                className="px-2 py-1 text-xs"
                style={{
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  background: layer === l ? 'var(--accent)' : 'transparent',
                  color: layer === l ? 'var(--accent-ink)' : 'var(--text-dim)',
                }}
              >
                {l}
              </button>
            ))}
          </>
        )}
        <span className="ml-auto text-[11px] text-text-faint">
          {shownPts((side === 'ours' ? h?.ours : h?.theirs) ?? []).length} kills
        </span>
      </div>
      <HeatCanvas
        map={layered && layer === 'lower' ? `${h?.map}_lower` : (h?.map ?? '')}
        pts={shownPts((side === 'ours' ? h?.ours : h?.theirs) ?? [])}
        ours={side === 'ours'}
      />
    </>
  )
}

function HeatCanvas({ map, pts, ours }: { map: string; pts: SeasonHeat['ours']; ours: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const cv = ref.current
    if (!cv) return
    const ctx = cv.getContext('2d')!
    const t = transformFor(map)
    ctx.clearRect(0, 0, 512, 512)
    let dead = false // ignore a late load after the deps changed (map/side/floor toggled fast)
    const radar = new Image()
    const paint = () => {
      if (dead) return
      ctx.clearRect(0, 0, 512, 512)
      ctx.fillStyle = 'rgba(10,12,16,1)'
      ctx.fillRect(0, 0, 512, 512)
      if (radar.complete && radar.naturalWidth) {
        ctx.save()
        ctx.globalAlpha = 0.4 // same reasoning as the replayer: the map is context, the marks are the point
        ctx.drawImage(radar, 0, 0, 512, 512)
        ctx.restore()
      }
      // Additive blobs: overlap is the signal — one death is noise, twenty in a doorway is a habit.
      ctx.globalCompositeOperation = 'lighter'
      for (const [x, y] of pts) {
        const cx = ((x - t.posX) / t.scale) * (512 / 1024)
        const cy = ((t.posY - y) / t.scale) * (512 / 1024)
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 14)
        g.addColorStop(0, ours ? 'rgba(120,190,255,0.5)' : 'rgba(255,90,90,0.5)')
        g.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.arc(cx, cy, 14, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalCompositeOperation = 'source-over'
    }
    radar.onload = paint
    radar.src = `/api/radar/${map}?v=cs2`
    paint()
    return () => {
      dead = true
    }
  }, [map, pts, ours])
  return (
    <canvas
      ref={ref}
      width={512}
      height={512}
      className="mx-auto mt-3 block w-full border border-border"
      style={{ borderRadius: 'var(--radius)', maxWidth: 560, aspectRatio: '1' }}
    />
  )
}

const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0)
/** "62% of 13" — the percentage AND the sample, because one without the other misleads. */
const cv = (b: { n: number; won: number }) => (b.n ? `${pct(b.won, b.n)}% of ${b.n}` : '—')

const REASON: Record<string, string> = {
  ct_killed: 'CT eliminated',
  t_killed: 'T eliminated',
  bomb_exploded: 'bomb',
  bomb_defused: 'defuse',
  time_ran_out: 'clock',
}

/** Rating per match, oldest → newest. Totals say who's good; this says who's improving. */
function Spark({ points }: { points: number[] }) {
  if (points.length < 2) return <span className="text-[10px] text-text-faint">{points.length ? '1 game' : '–'}</span>
  const w = 54
  const h = 16
  const lo = Math.min(...points, 0.6)
  const hi = Math.max(...points, 1.4)
  const x = (i: number) => (i / (points.length - 1)) * w
  const y = (v: number) => h - ((v - lo) / (hi - lo || 1)) * h
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')
  const up = points[points.length - 1] >= points[0]
  return (
    <svg width={w} height={h} className="block">
      {/* 1.00 baseline — the line that means "average" */}
      <line x1="0" y1={y(1)} x2={w} y2={y(1)} stroke="var(--border)" strokeWidth="1" strokeDasharray="2 2" />
      <path d={d} fill="none" stroke={up ? 'var(--accent)' : 'var(--loss)'} strokeWidth="1.5" />
      <circle cx={x(points.length - 1)} cy={y(points[points.length - 1])} r="1.8" fill={up ? 'var(--accent)' : 'var(--loss)'} />
    </svg>
  )
}

const WEP: Record<string, string> = {
  ak47: 'AK-47', m4a1: 'M4A4', m4a1_silencer: 'M4A1-S', awp: 'AWP', ssg08: 'Scout',
  usp_silencer: 'USP-S', glock: 'Glock', hkp2000: 'P2000', deagle: 'Deagle', fiveseven: 'Five-Seven',
  galilar: 'Galil', famas: 'FAMAS', mp9: 'MP9', mac10: 'MAC-10', p250: 'P250', tec9: 'Tec-9',
  sg556: 'SG 553', aug: 'AUG', ump45: 'UMP', xm1014: 'XM1014', nova: 'Nova', elite: 'Dualies',
}
const wep = (w: string) => WEP[w] ?? w.replace(/_/g, ' ')

/** Date range of the matches in scope — "since when" is part of reading a season. */
function span(d: Season | null): string {
  const ts = (d?.matches ?? []).map((m) => m.finishedAt).filter(Boolean) as number[]
  if (!ts.length) return '—'
  const f = (n: number) => new Date(n * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  return ts.length === 1 || Math.min(...ts) === Math.max(...ts) ? f(ts[0]) : `${f(Math.min(...ts))} – ${f(Math.max(...ts))}`
}

function Row({ k, v, good, warn }: { k: string; v: string; good?: boolean; warn?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-text-faint">{k}</span>
      <span
        className="tabular-nums"
        style={{ color: warn ? 'var(--loss)' : good === undefined ? 'var(--text-dim)' : good ? 'var(--accent)' : 'var(--loss)' }}
        title={warn ? 'Heavily one-sided — an opponent scouting you will key on this' : undefined}
      >
        {v}
      </span>
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border border-border bg-bg-2 p-3" style={{ borderRadius: 'var(--radius)' }}>
      <div className="text-[10px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className="font-display mt-1 text-xl tabular-nums text-text">{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-text-dim">{sub}</div>}
    </div>
  )
}
